import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { MapPin, Calendar, ArrowRight } from "lucide-react";
import FollowUsSection from "../components/FollowUsSection";
import Stats from "../components/Stats";
import { useArticles } from "../api/hooks/useArticles";
import { useStats } from "../api/hooks/useStats";
import { assetUrl } from "../api/client";
import useScrollToTop from "../hooks/useScrollToTop";
import {
  getTranslatedTitle,
  getFirstCountryName,
} from "../api/utils/translations";

const Home = () => {
  const { t, i18n } = useTranslation();
  useScrollToTop(); // Scroll to top on navigation
  const { data } = useArticles({ featured: true, limit: 6 });
  const featuredArticles = Array.isArray(data?.data) ? data.data : [];

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useStats({ showOnHomepage: true });
  const stats = statsData?.data || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80"
            alt="Travel landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            {t("home.title")}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sunset to-coral">
              {t("home.titleAccent")}
            </span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto">
            {t("home.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-oceanBlue to-palmGreen hover:from-oceanBlue/90 hover:to-palmGreen/90"
            >
              <Link to="/blog">{t("home.readStories")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Stats
            data={stats}
            gradient="from-sunset to-coral"
            gridCols="grid-cols-1 md:grid-cols-3"
            isLoading={statsLoading}
            error={statsError}
            language={i18n.language}
          />
        </div>
      </section>

      {/* Featured Posts from Directus */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t("home.featuredStories")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t("home.featuredSubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredArticles.map((post) => {
              const imgId = post.cover_image?.id || post.cover_image; // support id or object id
              const imgSrc = imgId
                ? assetUrl(imgId, { width: 800, height: 450, fit: "cover" })
                : undefined;
              const uiLang = (i18n.language || "en").split("-")[0];
              const country = getFirstCountryName(post.countries, "—", uiLang);
              const date = post.published_at
                ? new Date(post.published_at).toLocaleDateString()
                : "";
              const readTime = post.read_time || "";
              const title = getTranslatedTitle(
                post.translations,
                uiLang,
                t("common.untitled", { defaultValue: "Untitled" })
              );

              return (
                <Card
                  key={post.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-video overflow-hidden bg-muted">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        {t("common.noImage", { defaultValue: "No image" })}
                      </div>
                    )}
                  </div>
                  <CardHeader>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <span className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {country}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {date}
                      </span>
                    </div>
                    <CardTitle className="text-xl">{title}</CardTitle>
                    <CardDescription className="text-gray-600">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{readTime}</span>
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-primary hover:text-primary/90"
                      >
                        <Link to={`/blog/${post.id}`}>
                          {t("blog.readMore")}
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/blog">
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                {t("home.viewAllPosts")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Follow Us / Social */}
      <FollowUsSection gradient="from-terracotta to-sunset" />
    </div>
  );
};

export default Home;
